import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Rewrite stored image URLs to load correctly on the current domain.
 *
 * Problem: seeded/uploaded images are stored with the raw backend origin
 * (e.g. http://72.60.219.30:3001/uploads/...). When the web app is served
 * over HTTPS the browser blocks HTTP image requests as mixed content.
 *
 * Fix: any URL whose path contains /uploads/ has its origin replaced with
 * the current API base URL origin so the request goes to the right server
 * over the right protocol.
 */
const UPLOADS_RE = /\/uploads\//;

export function resolveImageUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  // If the URL contains /uploads/, rewrite origin to match the API base
  if (UPLOADS_RE.test(trimmed)) {
    const uploadsIdx = trimmed.indexOf('/uploads/');
    const pathPart = trimmed.substring(uploadsIdx); // e.g. /uploads/images/prod_x.jpg
    // Derive API origin from the Vite env variable (strip trailing /api/v1 etc.)
    const apiBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
    if (apiBase) {
      try {
        const origin = new URL(apiBase).origin;
        return `${origin}${pathPart}`;
      } catch {
        // fall through
      }
    }
    // Fallback: use current page origin (works when backend is on same domain)
    return `${window.location.origin}${pathPart}`;
  }

  return trimmed;
}

/**
 * Utility function to merge Tailwind CSS classes
 * Used by ShadCN components for conditional styling
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date to a human-readable string
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Truncate text to a specific length
 */
export function truncate(str: string, length: number): string {
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

/**
 * Sleep utility for async operations
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate a unique order number
 * Format: ORD-{timestamp}-{random}
 */
export function generateOrderNumber(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `ORD-${timestamp}-${random}`;
}
